import { useTranslation } from 'react-i18next'
import { EARN_DASHBOARD_STATE, EarnDashboardData, EarnStatisticsResponse } from '../../types'
import {
  EarnDashboardLifecycleState,
  EarnDashboardReady,
  UseEarnPageDataHandlers,
} from '../../types/dashboardState'
import { formatUsd, toMyEarnAssets } from '../../utils/utils'
import { useEarnDashboardQuery } from '../api/useEarnDashboardQuery'
import { useEarnStatisticsQuery } from '../api/useEarnStatisticsQuery'

export interface EarnDashboardBaseReady extends EarnDashboardReady {
  dashboard: EarnDashboardData
  statistics: EarnStatisticsResponse | undefined
}

export type EarnDashboardBaseData = EarnDashboardLifecycleState | EarnDashboardBaseReady

export const useEarnDashboardBaseData = ({
  onDeposit,
  onWithdraw,
}: UseEarnPageDataHandlers): EarnDashboardBaseData => {
  const { t } = useTranslation()
  const { data, isLoading } = useEarnDashboardQuery()
  const { data: statistics, isLoading: isStatisticsLoading } = useEarnStatisticsQuery()

  if (isLoading || !data) {
    return { dashboardState: EARN_DASHBOARD_STATE.LOADING }
  }

  if (data.state === EARN_DASHBOARD_STATE.NO_PRODUCTS) {
    return { dashboardState: data.state }
  }

  if (data.state === EARN_DASHBOARD_STATE.NO_WALLETS) {
    return { dashboardState: data.state, featuredProduct: data.featuredProduct }
  }

  const assets = [...data.earnAssets, ...data.comingSoonAssets].map(
    toMyEarnAssets(t, onDeposit, onWithdraw, statistics?.currencies ?? {})
  )

  return {
    dashboardState: data.state,
    dashboard: data,
    statistics,
    assets,
    isStatisticsLoading,
    summary: {
      allocatedFunds: formatUsd(data.fundsOverview.allocatedFunds),
      availableFunds: formatUsd(statistics?.totals.merchantMovableBalance),
    },
  }
}
